import React, { useEffect, useState } from 'react'
import { Card, Table, Button, Select, Form, Row, Col, Divider, Tag, message } from 'antd'
import useEducation from '../hooks/useEducation'
import { PlusOutlined, DeleteOutlined, FilePdfOutlined, PlayCircleOutlined, FileTextOutlined } from '@ant-design/icons'

const { Option } = Select

const PatientEducation = ({ value = [], onChange }) => {
  const [form] = Form.useForm()
  const { resources, loading } = useEducation()
  const [selectedResources, setSelectedResources] = useState(value)

  useEffect(() => {
    if (value && value.length > 0) {
      setSelectedResources(value)
    }
  }, [value])

  const handleAddResource = () => {
    form.validateFields().then((values) => {
      const selectedResource = resources.find((r) => r.id === values.resourceId)

      const newResource = {
        id: values.resourceId,
        name: selectedResource?.name || '',
        description: selectedResource?.description || '',
        type: selectedResource?.type || '',
        format: selectedResource?.format || '',
        url: selectedResource?.url || '',
        importance: values.importance,
        instructions: values.instructions,
        notes: values.notes
      }

      const updatedResources = [...selectedResources, newResource]
      setSelectedResources(updatedResources)
      onChange(updatedResources)

      form.resetFields()
      message.success('Education resource added successfully')
    })
  }

  const handleRemoveResource = (resourceId) => {
    const updatedResources = selectedResources.filter((r) => r.id !== resourceId)
    setSelectedResources(updatedResources)
    onChange(updatedResources)
    message.success('Education resource removed successfully')
  }

  const getFormatIcon = (format) => {
    switch (format?.toLowerCase()) {
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#ff4d4f' }} />
      case 'video':
        return <PlayCircleOutlined style={{ color: '#1890ff' }} />
      default:
        return <FileTextOutlined style={{ color: '#52c41a' }} />
    }
  }

  const columns = [
    {
      title: 'Resource',
      dataIndex: 'name',
      key: 'name',
      width: '15%',
      render: (text, record) => (
        <span>
          {getFormatIcon(record.format)} {text}
        </span>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '12%'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '20%'
    },
    {
      title: 'Format',
      dataIndex: 'format',
      key: 'format',
      width: '8%',
      render: (format) => <Tag color={format === 'PDF' ? 'red' : format === 'Video' ? 'blue' : 'green'}>{format}</Tag>
    },
    {
      title: 'Importance',
      dataIndex: 'importance',
      key: 'importance',
      width: '8%'
    },
    {
      title: 'Instructions',
      dataIndex: 'instructions',
      key: 'instructions',
      width: '18%'
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: '15%'
    },
    {
      title: 'Action',
      key: 'action',
      width: '6%',
      render: (_, record) => (
        <Button icon={<DeleteOutlined />} danger type='link' onClick={() => handleRemoveResource(record.id)} />
      )
    }
  ]

  return (
    <Card title='Patient Instructions & Education' bordered={false}>
      <Form form={form} layout='vertical'>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name='resourceId'
              label='Educational Resource'
              rules={[{ required: true, message: 'Please select a resource' }]}
            >
              <Select
                placeholder='Select educational resource'
                loading={loading}
                showSearch
                optionFilterProp='children'
              >
                {resources.map((resource) => (
                  <Option key={resource.id} value={resource.id}>
                    {getFormatIcon(resource.format)} {resource.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name='importance'
              label='Importance'
              rules={[{ required: true, message: 'Please select importance' }]}
            >
              <Select placeholder='Select importance'>
                <Option value='Essential'>Essential</Option>
                <Option value='Recommended'>Recommended</Option>
                <Option value='Optional'>Optional</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name='instructions'
              label='Instructions'
              rules={[{ required: true, message: 'Please add instructions' }]}
            >
              <Select placeholder='Select instructions'>
                <Option value='Review daily'>Review daily</Option>
                <Option value='Practice techniques shown'>Practice techniques shown</Option>
                <Option value='Complete all exercises'>Complete all exercises</Option>
                <Option value='Watch before next session'>Watch before next session</Option>
                <Option value='Discuss with therapist at next visit'>Discuss with therapist at next visit</Option>
                <Option value='Apply techniques as needed'>Apply techniques as needed</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name='notes' label='Notes'>
              <Select placeholder='Add notes'>
                <Option value='Focus on sections 1-3'>Focus on sections 1-3</Option>
                <Option value='Essential for home exercise program'>Essential for home exercise program</Option>
                <Option value='Supplements in-person therapy'>Supplements in-person therapy</Option>
                <Option value='Share with caregiver/family'>Share with caregiver/family</Option>
                <Option value='Explains proper technique'>Explains proper technique</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button type='primary' onClick={handleAddResource} icon={<PlusOutlined />}>
              Add Resource
            </Button>
          </Col>
        </Row>
      </Form>

      <Divider />

      <Table columns={columns} dataSource={selectedResources} rowKey='id' pagination={false} scroll={{ y: 300 }} />

      {selectedResources.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          No educational resources added yet. Use the form above to add patient education resources to the
          rehabilitation plan.
        </div>
      )}
    </Card>
  )
}

export default PatientEducation
